import {
  Button,
  Flex,
  Menu,
  MenuList,
  MenuButton,
  MenuItemOption,
  MenuOptionGroup,
  MenuDivider,
} from '@cdl/heartbeat-react';
import { intersection } from 'lodash';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { trackWaveTag } from '../../analytics/thunks/track-wave-tag.thunk';
import { TagEventType } from '../../analytics/types/analytics.types';
import {
  associativeTagsForWaveSelector,
  tagsForWaveSelector,
  waveForIdSelector,
} from '../../wave/selectors/wave.selectors';
import { setWavesTagsForType } from '../../wave/thunks/set-waves-tags-for-type.thunk';
import { setWavesTags } from '../../wave/thunks/set-waves-tags.thunk';
import { INVALID_WAVE_TYPES, Wave, WaveId } from '../../wave/types/wave.type';
import {
  getAssociativeTags,
  getOptionalTagContainers,
  getRequiredTags,
  isTagsEquals,
} from '../../wave/utils/tags.utils';
import { isWaveOfType } from '../../wave/utils/wave.util';
import { TagContainer, TagSelectionMode } from '../../wave/types/tag.type';
import { addWave } from '../../wave/thunks/add-wave.thunk';
import { exactDurationSelector } from '../selectors/ecg.selectors';

interface Props {
  waveId: WaveId;
  onClose: () => void;
  optionalTagSelectionMode?: TagSelectionMode;
}

export const TagEditor = ({
  waveId,
  onClose: closeContextMenu,
  optionalTagSelectionMode = 'multiple',
}: Props) => {
  const dispatch = useDispatch();
  const wave = useSelector(waveForIdSelector(waveId));
  const tagContainers = useSelector(tagsForWaveSelector(waveId));
  const associativeTagContainers = useSelector(
    associativeTagsForWaveSelector(waveId),
  );
  const ecgDuration = useSelector(exactDurationSelector);

  const handleApplyToAllClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      wave && dispatch(setWavesTagsForType(wave.tags, wave.type));
      closeContextMenu();
    },
    [dispatch, closeContextMenu, wave],
  );

  const handleApplyToFullLeadClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      if (wave == null) {
        return null;
      }
      const newWave: Wave = {
        ...wave,
        onset: 0,
        offset: ecgDuration,
      };
      dispatch(addWave(newWave));
      closeContextMenu();
    },
    [wave, closeContextMenu, dispatch, ecgDuration],
  );

  if (wave == null) {
    return null;
  }

  const tags = new Set(wave.tags);

  const associativeTags = getAssociativeTags(wave.tags);
  const optionalTags = getOptionalTagContainers(wave.type, wave.tags);
  const isBeatWave = isWaveOfType(wave, 'P', 'QRS', 'T');
  const isNoiseWave = isWaveOfType(wave, ...INVALID_WAVE_TYPES);

  if (associativeTagContainers.length === 0 && tagContainers.length === 0) {
    return null;
  }

  const onClickTag = (tag: string, type: TagEventType) => {
    dispatch(trackWaveTag(wave, tag, type));
  };

  const handleDelineationTagClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    delineationTag: TagContainer,
  ) => {
    e.preventDefault();
    const associative = intersection(Array.from(tags), associativeTags);
    const shouldRemove = isTagsEquals(
      delineationTag.tags,
      getRequiredTags(Array.from(tags)),
    );
    onClickTag(delineationTag.name, 'primary');
    dispatch(
      setWavesTags(
        shouldRemove
          ? [...associative]
          : [...delineationTag.tags, ...associative],
        waveId,
        wave.type,
      ),
    );
  };

  const handleMultipleOptionalTagClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    selectedTagContainer: TagContainer,
  ) => {
    e.preventDefault();
    selectedTagContainer.tags.forEach((tag) => {
      tags.delete(tag) || tags.add(tag);
      onClickTag(selectedTagContainer.name, 'secondary');
    });
    dispatch(setWavesTags(Array.from(tags), waveId, wave.type));
  };

  const handleSingleOptionalTagClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    selectedTagContainer: TagContainer,
  ) => {
    e.preventDefault();
    const allOptionalTags = optionalTags.flatMap(
      (tagContainer) => tagContainer.tags,
    );
    allOptionalTags.forEach((tag) => tags.delete(tag));
    selectedTagContainer.tags.forEach((tag) => {
      tags.add(tag);
      onClickTag(selectedTagContainer.name, 'secondary');
    });

    dispatch(setWavesTags(Array.from(tags), waveId, wave.type));
  };

  const handleAssociativeTagClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    tagContainer: TagContainer,
  ) => {
    e.preventDefault();
    tagContainer.tags.forEach((tag) => {
      tags.delete(tag) || tags.add(tag);
      onClickTag(tagContainer.name, 'noise');
    });
    dispatch(setWavesTags(Array.from(tags), waveId, wave.type));
  };

  const isDelineationTagSelected = (tagContainer: TagContainer) =>
    isTagsEquals(tagContainer.tags, getRequiredTags(Array.from(tags)));

  return (
    <MenuList data-testid='tag-editor-menu-list'>
      {associativeTagContainers.length > 0 && (
        <>
          {/* no wrap with <MenuOptionGroup> because it blocks from checking (isChecked) the item programmatically */}
          {associativeTagContainers.map((tagContainer) => (
            <MenuItemOption
              isChecked={tagContainer.tags.every((tag) => tags.has(tag))}
              onClick={(e) => handleAssociativeTagClick(e, tagContainer)}
              key={tagContainer.name}
            >
              {tagContainer.name}
            </MenuItemOption>
          ))}
          <MenuDivider color='neutral.100' />
        </>
      )}
      <MenuOptionGroup>
        {tagContainers.map((delineationTag) => (
          <Menu
            placement='right-start'
            closeOnSelect={false}
            key={delineationTag.name}
            isOpen={isDelineationTagSelected(delineationTag)}
          >
            <MenuButton
              as={MenuItemOption}
              isChecked={isDelineationTagSelected(delineationTag)}
              onClick={(e) => handleDelineationTagClick(e, delineationTag)}
            >
              {delineationTag.name}
            </MenuButton>
            <MenuList>
              <MenuOptionGroup type='checkbox' value={Array.from(tags)}>
                {optionalTags.map((tagContainer) => (
                  <MenuItemOption
                    onClick={(e) =>
                      optionalTagSelectionMode === 'multiple'
                        ? handleMultipleOptionalTagClick(e, tagContainer)
                        : handleSingleOptionalTagClick(e, tagContainer)
                    }
                    key={tagContainer.name}
                    value={tagContainer.tags[0]}
                  >
                    {tagContainer.name}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            </MenuList>
          </Menu>
        ))}
      </MenuOptionGroup>
      {isBeatWave && (
        <Flex
          justifyContent='flex-end'
          background='neutral.25'
          padding='s'
          borderTop='1px solid'
          borderColor='neutral.100'
        >
          <Button size='xs' onClick={handleApplyToAllClick}>
            Apply to all {wave.type}
          </Button>
        </Flex>
      )}
      {isNoiseWave && (
        <Flex
          justifyContent='flex-end'
          background='neutral.25'
          padding='s'
          borderTop='1px solid'
          borderColor='neutral.100'
        >
          <Button size='xs' onClick={handleApplyToFullLeadClick}>
            Apply to full lead
          </Button>
        </Flex>
      )}
    </MenuList>
  );
};
